import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BRIDGE_SUMATOR_LABEL_KEY,
    BridgeSumatorResponse,
    formatBridgeSumatorDescription,
    orderBridgeSumatorLines,
} from '../domain/bridgeSumator';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useSumator } from '../contexts/SumatorContext';
import useQuery from '../hooks/useQuery';
import useRequest from '../hooks/useRequest';

/**
 * Feeds the Sumator widget the lines a legacy operation would have written.
 *
 * In Delphi the Sumator is part of the Contracts form, and the form that
 * performs an operation appends its own line to it (`Ucontracte.pas`
 * `memSumator`): a new contract, a redemption, an extension or a goods sale.
 * A legacy operation replayed through the bridge has no form in this app, so
 * nothing appended anything - which is why the calculator never appeared after
 * a bridged operation.
 *
 * Renders nothing. It sits beside `SumatorWidget` rather than inside the
 * Contracts page because the widget itself is app-wide: mounting it on the page
 * would drop a line raised while the operator is on another screen, and
 * appending one opens the widget anyway.
 *
 * The poll is written by hand rather than with `useDeferredQuery` because the
 * cursor advances on every response: `useQuery` restarts its interval whenever
 * its params change, so a cursor passed as a param would reset the timer on
 * every tick. Both the cursor and the cash point are therefore read from refs
 * at call time, and the interval is started exactly once.
 *
 * Nothing is polled at all when the bridge is not configured, matching
 * `BridgeStatusIndicator`: a deployment outside the shadow rollout should not
 * pay for a feature it can never see.
 *
 * Reference: documentation/implementation_plan_legacy_delphi_bridge_api.md (section 7.7)
 */

/** Matched to the header's bridge poll, so a line lands with its status chip. */
const POLL_INTERVAL_MS = 2000;

const BridgeSumatorSync = () => {
    const { t } = useTranslation();
    const { addBridgeLine } = useSumator();
    const { cashPointId } = useCashDrawer();
    const request = useRequest();

    /**
     * The cursor for the next poll, taken from the previous response's clock.
     * Unset means "from now on", which the backend answers with an empty list
     * and a fresh cursor - so the widget starts empty the way the legacy memo
     * does at application start, instead of replaying the whole day into it.
     */
    const since = useRef<string | null>(null);

    /** Read at call time so a cash point resolved later is picked up. */
    const cashPointIdRef = useRef<number | null>(cashPointId);
    useEffect(() => {
        cashPointIdRef.current = cashPointId;
    }, [cashPointId]);

    /** Same, for the pieces the interval callback would otherwise close over stale. */
    const appendRef = useRef(addBridgeLine);
    const translateRef = useRef(t);
    useEffect(() => {
        appendRef.current = addBridgeLine;
        translateRef.current = t;
    }, [addBridgeLine, t]);

    // One cheap probe decides whether polling is worth starting at all.
    const { data: probe } = useQuery<BridgeSumatorResponse>({ path: '/legacy-bridge-activity' });
    const enabled = probe?.enabled === true;

    const poll = useCallback(async () => {
        const response = await request<BridgeSumatorResponse>({
            path: '/legacy-bridge-activity/sumator',
            params: {
                since: since.current ?? undefined,
                cashPointId: cashPointIdRef.current ?? undefined,
            },
        });

        if (!response?.enabled) {
            return;
        }

        for (const line of orderBridgeSumatorLines(response.lines ?? [])) {
            appendRef.current(
                line.eventId,
                formatBridgeSumatorDescription(
                    translateRef.current(BRIDGE_SUMATOR_LABEL_KEY[line.kind]),
                    line.contractNumber ?? line.reference,
                ),
                line.amount,
            );
        }

        since.current = response.serverTime;
    }, [request]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        // A failed poll must never surface: the bridge may not stop an operator
        // from working (plan section 7.3), and the next tick retries anyway.
        const tick = () => {
            poll().catch(error => console.error('Failed to read bridge sumator lines:', error));
        };

        tick();
        const interval = setInterval(tick, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [enabled, poll]);

    return null;
};

export default BridgeSumatorSync;
